
import React from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface FinanceiroMetricsCardProps {
  title: string
  value: number
  previousValue?: number
  format?: 'currency' | 'percentage' | 'number'
  icon: React.ReactNode
  color?: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'indigo'
}

export const FinanceiroMetricsCard: React.FC<FinanceiroMetricsCardProps> = ({
  title,
  value,
  previousValue,
  format = 'currency',
  icon,
  color = 'blue'
}) => {
  const formatValue = (val: number) => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(val)
      case 'percentage':
        return `${val.toFixed(1)}%`
      case 'number':
        return new Intl.NumberFormat('pt-BR').format(val)
      default:
        return val.toString()
    }
  }

  const calculateVariation = () => {
    if (previousValue === undefined || previousValue === 0) return null
    
    const variation = ((value - previousValue) / Math.abs(previousValue)) * 100
    return variation
  }

  const variation = calculateVariation()

  const getVariationIcon = () => {
    if (variation === null) return <Minus size={16} />
    if (variation > 0) return <TrendingUp size={16} />
    if (variation < 0) return <TrendingDown size={16} />
    return <Minus size={16} />
  }

  const getVariationColor = () => {
    if (variation === null) return 'text-gray-500'
    if (variation > 0) return 'text-green-600'
    if (variation < 0) return 'text-red-600'
    return 'text-gray-500'
  }

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
    indigo: 'bg-indigo-500'
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]} text-white`}>
          {icon}
        </div>
        {variation !== null && (
          <div className={`flex items-center gap-1 ${getVariationColor()}`}>
            {getVariationIcon()}
            <span className="text-sm font-medium">
              {Math.abs(variation).toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <p className="text-2xl font-bold text-gray-900">
          {formatValue(value)}
        </p>
        {previousValue !== undefined && (
          <p className="text-xs text-gray-500">
            Mês anterior: {formatValue(previousValue)}
          </p>
        )}
      </div>
    </div>
  )
}

export default FinanceiroMetricsCard
